// src/utils/apiUtils.js

/**
 * Standardizes API response handling
 * @param {Object} response - The raw API response
 * @returns {Object} The standardized data from the response
 */
export const normalizeResponse = (response) => {
    // Check if response has a data property with relevant content inside
    // if (response && response.data) {
    //   // Return the inner data object
    //   return response.data;
    // }
    
    // If response doesn't have data property, return the raw response
    return response;
  };
  
  /**
   * Extracts error message from various error objects
   * @param {Error|Object} error - Error object
   * @returns {string} Extracted error message
   */
  export const extractErrorMessage = (error) => {
    // Try to extract error message following different patterns
    return error.response?.data?.message || 
           error.message || 
           (typeof error === 'string' ? error : 'An unknown error occurred');
  };


  /**
 * Format pagination parameters for API calls
 * @param {Object} params - Pagination parameters
 * @returns {Object} Formatted parameters
 */
export const formatPaginationParams = (params) => {
  const { page, limit } = params;
  
  return {
    page: page || 1,
    limit: limit || 10
  };
};

/**
 * Format filter parameters for API calls
 * @param {Object} filters - Filter parameters
 * @returns {Object} Formatted parameters
 */
export const formatFilterParams = (filters) => {
  if (!filters) return {};
  
  // Remove 'all' values from filters
  const formattedFilters = {};
  
  Object.entries(filters).forEach(([key, value]) => {
    // Skip 'all', empty strings, null, or undefined values
    if (value && value !== 'all' && value !== '') {
      formattedFilters[key] = value;
    }
  });
  
  return formattedFilters;
};

/**
 * Format search parameter for API calls
 * @param {string} searchTerm - Search term
 * @returns {Object} Formatted parameters
 */
export const formatSearchParam = (searchTerm) => {
  if (!searchTerm) return {};
  
  return { search: searchTerm };
};

/**
 * Format sort parameter for API calls
 * @param {string} field - Sort field
 * @param {string} direction - Sort direction ('asc' or 'desc')
 * @returns {Object} Formatted parameters
 */
export const formatSortParam = (field, direction) => {
  if (!field) return {};
  
  const sortParam = direction === 'desc' ? `-${field}` : field;
  
  return { sort: sortParam };
};

/**
 * Combine all parameters for API calls
 * @param {Object} paginationParams - Pagination parameters
 * @param {Object} filterParams - Filter parameters
 * @param {string} searchTerm - Search term
 * @param {string} sortField - Sort field
 * @param {string} sortDirection - Sort direction
 * @returns {Object} Combined parameters
 */
export const combineQueryParams = ({
  paginationParams = {},
  filterParams = {},
  searchTerm = '',
  sortField = 'name',
  sortDirection = 'asc'
}) => {
  return {
    ...formatPaginationParams(paginationParams),
    ...formatFilterParams(filterParams),
    ...formatSearchParam(searchTerm),
    ...formatSortParam(sortField, sortDirection)
  };
};

/**
 * Convert query params object to query string
 * @param {Object} params - Query parameters
 * @returns {string} Query string
 */
export const objectToQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  
  const queryString = Object.entries(params)
    // .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .filter(([_, value]) => value !== undefined && value !== '')
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');
  
  return queryString ? `?${queryString}` : '';
};

export default {
  normalizeResponse,
  extractErrorMessage,
  formatPaginationParams,
  formatFilterParams,
  formatSearchParam,
  formatSortParam,
  combineQueryParams,
  objectToQueryString
};